import Layout from "../components/layout/Layout";
import Hero from "../components/ui/Hero";
import Breadcrumb from "../components/ui/Breadcrumb";
import SeoMeta from "../components/common/SeoMeta";
import careers from "../content/careers/careers.json";
import {
  FaMapMarkerAlt,
  FaBriefcase,
  FaClock,
  FaPoundSign,
  FaCalendarAlt,
} from "react-icons/fa";
import ApplicationForm from "../components/careers/ApplicationForm";

const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL || "https://www.srktrends.com";

// ✅ Small reusable block: icon + label + value
function Detail({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3">
      <Icon className="mt-1 text-yellow-500 shrink-0" />
      <div>
        <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
        <p className="font-medium text-gray-900">{value}</p>
      </div>
    </div>
  );
}

// ✅ Small reusable block: heading + bullet list
function BulletSection({ title, items = [] }) {
  if (!items.length) return null;
  return (
    <section className="mt-10">
      <h3 className="text-xl font-bold text-gray-900 mb-4">{title}</h3>
      <ul className="space-y-2 text-gray-700 leading-relaxed list-disc pl-5 marker:text-yellow-500">
        {items.map((item, idx) => (
          <li key={idx}>{item}</li>
        ))}
      </ul>
    </section>
  );
}

// ✅ One full vacancy
function JobCard({ job, applyEmail }) {

  return (
    <article
      id={job.id}
      className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden"
    >
      {/* Title strip */}
      <div className="bg-yellow-50 border-b border-yellow-200 px-6 sm:px-10 py-8">
        <p className="text-sm font-semibold uppercase tracking-wide text-yellow-700 mb-2">
          We&apos;re hiring
        </p>
        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">{job.title}</h2>
        {job.company && <p className="mt-2 text-gray-600">{job.company}</p>}
      </div>

      {/* Key details */}
      <div className="px-6 sm:px-10 py-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 border-b border-gray-200">
        <Detail icon={FaMapMarkerAlt} label="Location" value={job.location} />
        <Detail icon={FaBriefcase} label="Employment type" value={job.type} />
        <Detail icon={FaClock} label="Working hours" value={job.hours} />
        <Detail icon={FaPoundSign} label="Salary" value={job.salary} />
        <Detail icon={FaCalendarAlt} label="Closing date" value={job.closingDate} />
        <Detail icon={FaBriefcase} label="Reports to" value={job.reportsTo} />
      </div>

      {/* Body */}
      <div className="px-6 sm:px-10 py-8">
        {job.whyHiring && (
          <section>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Why are we hiring?</h3>
            <p className="text-gray-700 leading-relaxed">{job.whyHiring}</p>
          </section>
        )}

        {job.overview && (
          <section className="mt-10">
            <h3 className="text-xl font-bold text-gray-900 mb-3">Role overview</h3>
            <p className="text-gray-700 leading-relaxed">{job.overview}</p>
          </section>
        )}

        <BulletSection title="Key responsibilities" items={job.responsibilities} />
        <BulletSection title="Skills and experience" items={job.requirements} />
        <BulletSection title="Benefits" items={job.benefits} />

        {/* How to apply */}
        <section className="mt-12 bg-gray-50 border border-gray-200 rounded-xl p-6 sm:p-8">
          <h3 className="text-xl font-bold text-gray-900 mb-3">How to apply</h3>
          {job.howToApply && (
            <p className="text-gray-700 leading-relaxed mb-6">{job.howToApply}</p>
          )}
                    <ApplicationForm jobTitle={job.title} />
          {job.closingDate && (
            <p className="mt-4 text-sm text-gray-500">Applications close on {job.closingDate}.</p>
          )}
        </section>
      </div>
    </article>
  );
}

export default function CareersPage() {
  return (
    <Layout>
      <SeoMeta
        title="Careers | SRK Trends"
        description="Join the team at SRK Trends in Sparkhill, Birmingham. View our current vacancies and apply today."
        keywords="SRK Trends careers, retail manager job Birmingham, Sparkhill jobs"
        url={`${SITE_URL}/careers`}
        image={`${SITE_URL}${careers.heroImage}`}
      />

      {/* ✅ Hero banner (same component as the other pages) */}
      <Hero title={careers.title} subtitle={careers.subtitle} image={careers.heroImage} />

      {/* ✅ Breadcrumb */}
      <Breadcrumb path={careers.breadcrumb} />

      {/* ✅ Intro + vacancies */}
      <div className="container mx-auto px-6 pb-16">
        <div className="max-w-4xl mx-auto">
          {careers.intro && (
            <p className="text-lg text-gray-700 leading-relaxed mb-10">{careers.intro}</p>
          )}

          {careers.jobs.length === 0 ? (
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-8 text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">No vacancies right now</h2>
              <p className="text-gray-700">
                Check back soon, or send your CV to{" "}
                <a href={`mailto:${careers.applyEmail}`} className="underline">
                  {careers.applyEmail}
                </a>{" "}
                and we&apos;ll keep it on file.
              </p>
            </div>
          ) : (
            <div className="space-y-12">
              {careers.jobs.map((job) => (
                <JobCard key={job.id} job={job} applyEmail={careers.applyEmail} />
              ))}
            </div>
          )}

          {careers.equalOpportunities && (
            <p className="mt-10 text-sm text-gray-500 text-center">{careers.equalOpportunities}</p>
          )}
        </div>
      </div>
    </Layout>
  );
}